const pad = (num: number): string => String(num).padStart(2, "0");
/**
 * Formats an ISO date string according to a specified format.
 *
 * @param isoDateString The input ISO 8601 date string (e.g., '2025-09-22T08:47:15.627Z') or javascript Date object
 * @param format The format string (e.g., 'd/m/Y H:i:s')
 * - d / DD: Day (01-31)
 * - m / MM: Month (01-12)
 * - Y / YYYY: Full Year (e.g., 2025)
 * - H: Hour (00-23)
 * - i: Minute (00-59)
 * - s: Second (00-59)
 * @returns A formatted date string.
 */
export function formatISODate(isoDate: string | Date, format: string = "d/m/Y H:i"): string {
  const date = new Date(isoDate);

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }
  // Note: .getMonth() is 0-indexed (0-11), so we add 1.
  const parts = {
    d: pad(date.getDate()),
    m: pad(date.getMonth() + 1),
    Y: String(date.getFullYear()),
    H: pad(date.getHours()),
    i: pad(date.getMinutes()),
    s: pad(date.getSeconds())
  };

  const tokenMap: { [key: string]: PartKey } = {
    // Day
    d: "d",
    DD: "d",
    // Month
    m: "m",
    MM: "m",
    // Year
    Y: "Y", // 'Y' and 'YYYY' will both map to the 4-digit year
    YYYY: "Y",
    // Hour
    H: "H",
    // Minute
    i: "i",
    // Second
    s: "s"
  };

  // Define the type for our map keys for type safety
  type PartKey = keyof typeof parts;

  return format.replace(/YYYY|Y|MM|m|DD|d|H|i|s/g, (match) => {
    const partKey = tokenMap[match];
    return partKey ? parts[partKey] : match;
  });
}
